import { useEffect, useState } from "react";
import { CssBaseline } from "@mui/material";
import { Suspense } from "react";
import Router from "./routes";
import ThemeConfig from "./theme";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import SessionCheck from "pages/Session/SessionCheck";
import { Loader, NewUpdateModal } from "empower-energy-owner-components";
import { getProfile } from "actions/profile";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "react-phone-number-input/style.css";
import { MFAOtpModal, MFASetupModal } from "components";
import axios from "axios";
import { getError } from "utils/errors";
import Maintenance from "pages/Static/Maintenance";
import { clearVersion, getVersion, reconcileVersion } from "actions/version";

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response.status !== 401) {
      getError(error.response.data);
    }
    return Promise.reject(error);
  }
);

function App() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [openMfaSetup, setOpenMfaSetup] = useState(false);
  const [openMfaOtpModal, setOpenMfaOtpModal] = useState(false);

  const error = useSelector((state) => state.session.error);
  const user = useSelector((state) => state.session.user);
  const session = useSelector((state) => state.session.session);
  const updateLoading = useSelector((state) => state.session.updateLoading);

  const isNewVersion = useSelector((state) => state.version?.isNewVersion);
  const newVersionDescription = useSelector(
    (state) => state.version?.description
  );
  const maintenance = useSelector((state) => state.version?.maintenance);

  useEffect(() => {
    if (error?.code === "PasswordResetRequiredException") {
      navigate("/reset-password");
    }
  }, [error]);

  useEffect(() => {
    if (user?.challengeName === "SMS_MFA") {
      if (!openMfaOtpModal) setOpenMfaOtpModal(true);
    } else if (user?.challengeName === "MFA_SETUP") {
      setOpenMfaSetup(true);
    } else if (user?.challengeName === "SOFTWARE_TOKEN_MFA") {
      setOpenMfaOtpModal(true);
    } else if (user?.challengeName === "NEW_PASSWORD_REQUIRED") {
      navigate("/first-login");
    } else {
      setOpenMfaSetup(false);
      setOpenMfaOtpModal(false);
      if (
        session &&
        ["/", "/login", "/first-login", "/change-password"].includes(
          location.pathname
        )
      ) {
        navigate("/home");
      }
    }
  }, [user, session]);

  useEffect(() => {
    if (!updateLoading && user) {
      if (user?.challengeName === "SMS_MFA") {
        setOpenMfaOtpModal(true);
      }
    }
  }, [user, updateLoading]);

  useEffect(() => {
    if (session) {
      dispatch(getProfile());
    }
  }, [session, dispatch]);

  useEffect(() => {
    dispatch(getVersion());
    const interval = setInterval(() => {
      dispatch(getVersion());
    }, 1000 * 60 * 15);

    return () => {
      clearInterval(interval);
    };
  }, [dispatch]);

  const updateApp = () => {
    dispatch(
      reconcileVersion(() => {
        setTimeout(function () {
          window.location.reload();
        }, 100);
      })
    );
  };

  const clearNewVersion = () => {
    dispatch(clearVersion());
  };

  return (
    <ThemeConfig>
      <CssBaseline />
      <SessionCheck />
      <Suspense fallback={<Loader show={true} fullPage={true} />}>
        <Router />
      </Suspense>
      <ToastContainer
        autoClose={3000}
        pauseOnFocusLoss={false}
        pauseOnHover={false}
      />
      {openMfaSetup && (
        <MFASetupModal
          open={openMfaSetup}
          onClose={() => setOpenMfaSetup(false)}
        />
      )}
      {openMfaOtpModal && (
        <MFAOtpModal
          open={openMfaOtpModal}
          onClose={() => setOpenMfaOtpModal(false)}
        />
      )}

      {isNewVersion && (
        <NewUpdateModal
          open={isNewVersion}
          description={newVersionDescription}
          onClose={clearNewVersion}
          onClickRefresh={updateApp}
        />
      )}
      {maintenance && <Maintenance />}
    </ThemeConfig>
  );
}

export default App;
